import { Fragment, useState } from "react";
import { 
  Dialog, 
  DialogPanel, 
  DialogTitle,
  Transition, 
  TransitionChild,
} from "@headlessui/react";
import { 
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { 
  Button, 
  Card,
} from "components/ui";
import { Input, Select, Radio, Checkbox } from "components/ui/Form";
import { EmailSampleModal } from "./EmailSampleModal";
import { AddCustomFieldModal } from "./AddCustomFieldModal";

export function ManageJobTypesModal({ show, onClose }) {
  const [contractType, setContractType] = useState("Fixed price");
  const [showEmailSample, setShowEmailSample] = useState(false);
  const [showAddCustomField, setShowAddCustomField] = useState(false);

  return (
    <>
      <Transition show={show} as={Fragment}>
        <Dialog as="div" className="relative z-[400]" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity dark:bg-black/60" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-hidden text-left">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all dark:bg-dark-750 border border-slate-100 dark:border-dark-600 isolate">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-dark-600 bg-slate-50/50">
                    <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                      Jobs
                    </DialogTitle>
                    <button
                      onClick={onClose}
                      className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-500 dark:text-dark-300 dark:hover:bg-dark-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/30 dark:bg-dark-800/30 no-scrollbar scrollbar-sm">
                    {/* Job Types */}
                    <section className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest pl-1">
                            Job Types <span className="text-slate-400">ⓘ</span>
                        </label>
                        <Input defaultValue="Custom New Home" className="max-w-md h-10" />
                    </section>

                    {/* Contract Type */}
                    <section className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest pl-1">
                            Contract Type <span className="text-slate-400">ⓘ</span>
                        </label>
                        <div className="space-y-4 pl-1">
                            <div className="flex flex-col gap-1">
                                <Radio 
                                    label={<span className="text-sm font-bold text-slate-700 dark:text-dark-200">Fixed price</span>} 
                                    checked={contractType === "Fixed price"} 
                                    onChange={() => setContractType("Fixed price")} 
                                />
                                <p className="text-[10px] text-amber-600 dark:text-amber-500/80 font-medium ml-7">You will set the contract price for the client</p>
                            </div>
                            <div className="flex flex-col gap-1">
                                <Radio 
                                    label={<span className="text-sm font-bold text-slate-700 dark:text-dark-200">Open book</span>} 
                                    checked={contractType === "Open book"} 
                                    onChange={() => setContractType("Open book")} 
                                />
                                <p className="text-[10px] text-slate-400 dark:text-dark-500 font-medium ml-7 leading-relaxed">Price = projected costs + markup. &quot;Cost plus&quot; and &quot;time and materials&quot; contracts should be categorized as &quot;open book.&quot;</p>
                            </div>
                        </div>
                        <div className="pt-2 flex flex-col gap-3 pl-1">
                            <Checkbox label={<span className="text-xs font-bold text-slate-700 dark:text-dark-200">Include Time Clock Labor in the Job Costing Budget on new jobs <span className="text-slate-400 font-normal">ⓘ</span></span>} checked />
                            <Button variant="flat" size="sm" className="w-fit text-[10px] h-7 border border-slate-200 dark:border-dark-600 bg-white dark:bg-dark-700 shadow-sm font-black uppercase tracking-tight">Update Existing Jobs</Button>
                        </div>
                    </section>

                    {/* Other Settings */}
                    <section className="space-y-3 border-t border-slate-100 dark:border-dark-600 pt-6">
                        <h3 className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest pl-1">Other Settings</h3>
                        <div className="space-y-2 pl-1">
                             <label className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest pl-1">
                                Default Cost Setting <span className="text-slate-400">ⓘ</span>
                            </label>
                            <Select data={["Line items", "Cost codes"]} defaultValue="Line items" className="max-w-xs" />
                        </div>
                    </section>

                    {/* Client Invitation Email */}
                    <section className="space-y-3 border-t border-slate-100 dark:border-dark-600 pt-6">
                        <h3 className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest pl-1">Client Invitation Email</h3>
                        <div className="space-y-4 pl-1">
                            <div className="flex items-center gap-3">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" />
                                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                                </label>
                                <span className="text-xs font-bold text-slate-700 dark:text-dark-200">Customize client email invite text</span>
                            </div>
                            <div className="p-5 bg-white dark:bg-dark-800 border border-slate-100 dark:border-dark-600 rounded-xl space-y-4 shadow-sm">
                                <p className="text-[11px] text-slate-600 dark:text-dark-300 leading-relaxed font-medium">
                                    We&apos;re excited to be working with you on your project, and we want to keep you up to date. We use Sunny Homes for that. With Sunny Homes, you can track the progress of your project, see updates and photos, and interact with our team.
                                </p>
                                <p className="text-[11px] text-slate-600 dark:text-dark-300 leading-relaxed font-medium">
                                    Activating your account is free and takes less than a minute. Let us know if you have any questions as you&apos;re getting started.
                                </p>
                                <Button 
                                    variant="flat" 
                                    size="sm" 
                                    className="text-[10px] font-black border border-slate-100 dark:border-dark-600 shadow-sm uppercase h-7"
                                    onClick={() => setShowEmailSample(true)}
                                >
                                    Email Me a Sample
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* Custom Fields */}
                    <section className="space-y-3 border-t border-slate-100 dark:border-dark-600 pt-6">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="text-[10px] font-black text-slate-400 dark:text-dark-400 uppercase tracking-widest">Jobs (Job Info) custom fields</h3>
                            <Button 
                                variant="flat" 
                                size="sm" 
                                className="h-8 border border-slate-100 dark:border-dark-600 bg-white dark:bg-dark-700 text-[10px] font-black uppercase shadow-sm"
                                onClick={() => setShowAddCustomField(true)}
                            >
                                <PlusIcon className="h-3 w-3 mr-1" /> Custom field
                            </Button>
                        </div>
                        <Card className="flex flex-col items-center justify-center p-12 text-center bg-white dark:bg-dark-800/20 border-dashed border-2 border-slate-200 dark:border-dark-700 rounded-2xl">
                             <h4 className="text-sm font-bold text-slate-900 dark:text-white">No custom fields</h4>
                             <p className="text-[10px] text-slate-500 dark:text-dark-400 mt-1 max-w-[240px] leading-relaxed">
                                Custom fields have not been added. Add data to your Jobs using custom fields.
                             </p>
                        </Card>
                    </section>
                  </div>

                  <div className="border-t border-slate-100 dark:border-dark-600 p-4 bg-white dark:bg-dark-800/50 flex justify-end shrink-0">
                    <Button variant="filled" color="primary" className="px-10 font-bold h-10 shadow-lg shadow-primary/20" onClick={onClose}>
                        Save
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <EmailSampleModal show={showEmailSample} onClose={() => setShowEmailSample(false)} />
      <AddCustomFieldModal show={showAddCustomField} onClose={() => setShowAddCustomField(false)} />
    </>
  );
}
